import { cn } from '@/utils/cn'

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  variant?: 'default' | 'elevated' | 'outlined'
  padding?: 'none' | 'sm' | 'md' | 'lg'
  interactive?: boolean
}

const Card = ({
  children,
  variant = 'default',
  padding = 'md',
  interactive = false,
  className,
  ...props
}: CardProps) => {
  const variants = {
    default: 'bg-white shadow-card',
    elevated: 'bg-white shadow-card-hover',
    outlined: 'bg-white border-2 border-gray-200'
  }

  const paddings = {
    none: '',
    sm: 'p-3',
    md: 'p-4',
    lg: 'p-6'
  }

  return (
    <div
      className={cn(
        'rounded-xl overflow-hidden',
        variants[variant],
        paddings[padding],
        interactive && 'cursor-pointer hover:shadow-card-hover transition-shadow active:scale-[0.99]',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

// Card sub-components
Card.Header = function CardHeader({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('flex items-center justify-between mb-4', className)} {...props}>
      {children}
    </div>
  )
}

Card.Title = function CardTitle({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3 className={cn('text-lg font-semibold text-gray-900', className)} {...props}>
      {children}
    </h3>
  )
}

Card.Content = function CardContent({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('', className)} {...props}>
      {children}
    </div>
  )
}

Card.Footer = function CardFooter({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('flex items-center justify-between mt-4 pt-4 border-t border-gray-100', className)} {...props}>
      {children}
    </div>
  )
}

export default Card
